import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { CollapsibleSection, ManifestRowsScroller, ManifestRowsTableHead } from './ManifestRowsTable';
import type { RowCommitState } from '../../commit';
import type { PreviewColumn, PreviewRowCells } from '../../state/api/organisationEngineSlice';

type FailedRowsTableProps = {
    rows: RowCommitState[];
    columns: PreviewColumn[];
    /** Engine-parsed cells looked up by row number — supplied by the parent
     * which has the preview state. Missing entries fall back to an empty row. */
    cellsByRowNumber: Map<number, PreviewRowCells>;
};

// Lists failed rows with their original CSV cells so Ops can identify which
// row failed — "Row 17" alone is unactionable when the manifest is hundreds
// of rows. The engine's error message renders beneath each row in a
// full-width band so it stays legible regardless of length.
export const FailedRowsTable = ({ rows, columns, cellsByRowNumber }: FailedRowsTableProps) => (
    <CollapsibleSection
        testId='commit-failed-rows'
        title={`${rows.length} ${rows.length === 1 ? 'row' : 'rows'} failed`}
        description='These rows did not onboard. The engine error for each is shown beneath the row.'
        defaultOpen
    >
        <ManifestRowsScroller>
            <table className='w-full border-collapse text-sm'>
                <ManifestRowsTableHead columns={columns} />
                {rows.map(row => (
                    <FailedRow key={row.rowNumber} state={row} columns={columns} cells={cellsByRowNumber.get(row.rowNumber)} />
                ))}
            </table>
        </ManifestRowsScroller>
    </CollapsibleSection>
);

// One `<tbody>` per failed row groups the cells row + the message row under
// a single testId so e2e assertions can target a logical row irrespective
// of how many `<tr>`s it spans.
const FailedRow = ({ state, columns, cells }: { state: RowCommitState; columns: PreviewColumn[]; cells: PreviewRowCells | undefined }) => {
    const colSpan = columns.length + 1;

    return (
        <tbody data-testid={`commit-failed-row-${state.rowNumber}`} className='border-t border-grey-20'>
            <tr className='align-top'>
                <td className='p-2 font-mono text-grey-70'>{state.rowNumber}</td>
                {columns.map(col => {
                    const value = cells?.[col.key] ?? '';
                    return (
                        <td key={col.key} className='p-2'>
                            <div className={`font-mono text-sm ${value === '' ? 'italic text-grey-70' : 'text-grey-100'}`}>
                                {value === '' ? '—' : value}
                            </div>
                        </td>
                    );
                })}
            </tr>
            <tr>
                <td />
                <td colSpan={colSpan - 1} className='px-2 pb-2 border-l-4 border-red-100'>
                    <div className='flex items-start gap-1 text-xs italic text-red-100'>
                        <AlertTriangle className='h-3 w-3 mt-0.5 shrink-0' aria-hidden='true' />
                        <span>{state.errorMessage}</span>
                    </div>
                </td>
            </tr>
        </tbody>
    );
};
